import React, { useState, useEffect } from 'react';
import { User, Bell, Shield, Settings as SettingsIcon, Mail, Phone, MapPin, Save, Building2, CreditCard, Lock, Plus, Camera, Edit, X, Hash, LogOut } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import Config from '../services/config.json';

const Settings = () => {
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [selectedClient, setSelectedClient] = useState();
  const [addresses, setAddresses] = useState([]);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [emirates, setEmirates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedEmirate, setSelectedEmirate] = useState(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [newAddress, setNewAddress] = useState({
    emirate: '',
    location: '',
    contactNo: '',
    address: '',
    poBox: '',
    refNo: '',
    isDefault: false,
    locationUrl: '',
    emirateId: null,
    locationId: null
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    mobileNo1: '',
    mobileNo2: '',
    telNo: '',
    company: '',
    emailId: '',
    payMode: 'CASH',
    photo: '',
    addresses: [],
    bankDetails: {
      bankName: '',
      bankAccNo: '',
      bankAccName: '',
      branchName: '',
      ibanNo: '',
      swiftCode: ''
    },
    password: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    notifications: {
      email: true,
      sms: false,
      push: true,
      deliveryUpdates: true,
      promotions: false
    },
    preferences: {
      language: 'English',
      theme: 'Light'
    }
  });

  // Fetch client details on component mount
  useEffect(() => {
    fetchClientDetails();
  }, []);

  // Auto-trigger logout confirmation when sign-out tab is selected
  useEffect(() => {
    if (activeTab === 'sign-out') {
      setShowLogoutConfirm(true);
    }
  }, [activeTab]);

  // Load emirates when address form is opened
  useEffect(() => {
    if (showAddAddressForm && emirates.length === 0) {
      loadEmirates();
    }
  }, [showAddAddressForm]);

  // Load locations when emirate is selected
  useEffect(() => {
    if (selectedEmirate) {
      loadLocations(selectedEmirate.Id);
    }
  }, [selectedEmirate]);

  // Close location dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-dropdown-container')) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      if (userData != null) {
        setSelectedClient(userData);
      }
      else {
        showError('Client ID not found');
        return;
      }

      const response = await MainService.ClientById(userData.ClientId, userData.Token);
      
      console.log('ClientById response:', response);
      console.log('Config.API_BASE_URL:', Config.API_BASE_URL);

      if (response) {
        // Handle different response structures
        const clientData = response.data || response.Data || response;
        console.log('Client data:', clientData);
        
        setFormData(prev => ({
          ...prev,
          name: clientData.Name || '',
          mobileNo1: clientData.MobileNo1 || '',
          mobileNo2: clientData.MobileNo2 || '',
          telNo: clientData.TelNo || '',
          company: clientData.Company || '',
          emailId: clientData.EmailId || '',
          payMode: clientData.PayMode || 'CASH',
          photo: clientData.Photo ? Config.API_BASE_URL + clientData.Photo : '',
          bankDetails: {
            bankName: clientData.BankName || '',
            bankAccNo: clientData.BankAccNo || '',
            bankAccName: clientData.BankAccName || '',
            branchName: clientData.BranchName || '',
            ibanNo: clientData.IBANNO || '',
            swiftCode: clientData.SwiftCode || ''
          }
        }));

        // Set addresses from ClientAddresses
        if (clientData.ClientAddresses && Array.isArray(clientData.ClientAddresses)) {
          setAddresses(clientData.ClientAddresses);
        }
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
      showError('Failed to fetch client details');
    } finally {
      setLoading(false);
    }
  };

  // Refresh addresses list
  const refreshAddresses = async () => {
    try {
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      
      if (!userData || !userData.Token) {
        showError('Please login first');
        return;
      }

      const response = await MainService.ClientById(userData.ClientId, userData.Token);
      
      if (response) {
        const clientData = response.data || response.Data || response;
        
        // Update addresses from ClientAddresses
        if (clientData.ClientAddresses && Array.isArray(clientData.ClientAddresses)) {
          setAddresses(clientData.ClientAddresses);
        }
      }
    } catch (error) {
      console.error('Error refreshing addresses:', error);
      showError('Failed to refresh addresses');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
    setFormData(prev => ({
      ...prev,
          photo: base64String
        }));
        showSuccess('Photo uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNewAddressChange = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Load emirates from API
  const loadEmirates = async () => {
    try {
      const response = await MainService.EmiratesInDropDown();
      if (response && Array.isArray(response)) {
        setEmirates(response);
      }
    } catch (error) {
      console.error('Error loading emirates:', error);
      showError('Failed to load emirates');
    }
  };

  // Load locations by emirate ID
  const loadLocations = async (emirateId) => {
    try {
      const response = await MainService.LocationByEmirateId(emirateId);
      if (response && Array.isArray(response)) {
        setLocations(response);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      showError('Failed to load locations');
    }
  };

  // Handle emirate selection
  const handleEmirateSelect = (emirate) => {
    setSelectedEmirate(emirate);
    setNewAddress(prev => ({
      ...prev,
      emirate: emirate.Name,
      emirateId: emirate.Id,
      location: '',
      locationId: null
    }));
    setLocationSearchQuery('');
    setLocations([]);
  };

  // Handle location search and selection
  const handleLocationSearch = (query) => {
    setLocationSearchQuery(query);
    setShowLocationDropdown(true);
  };

  const handleLocationSelect = (location) => {
    setNewAddress(prev => ({
      ...prev,
      location: location.Name,
      locationId: location.Id
    }));
    setLocationSearchQuery(location.Name);
    setShowLocationDropdown(false);
  };

  // Filter locations based on search query
  const filteredLocations = locations.filter(location =>
    location.Name.toLowerCase().includes(locationSearchQuery.toLowerCase())
  );

  const handleAddAddress = async () => {
    if (!newAddress.emirateId || !newAddress.locationId || !newAddress.address) {
      showError('Please fill in required fields (Emirate, Location, Address)');
      return;
    }

    try {
      setLoading(true);
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      
      if (!userData || !userData.Token) {
        showError('Please login first');
        return;
      }

      const addressData = {
        Id: 0, // New address
        POBOX: newAddress.poBox || '',
        ContactNo: newAddress.contactNo || '',
        Address: newAddress.address,
        LocationUrl: newAddress.locationUrl || '',
        IsDefault: newAddress.isDefault,
        ClientId: userData.ClientId,
        LocationId: newAddress.locationId,
        EmirateId: newAddress.emirateId,
        RefNo: newAddress.refNo || ''
      };

      const response = await MainService.SaveClientAddress(addressData, userData.Token);
      
      if (response) {
        // Refresh addresses list from API
        await refreshAddresses();
        
        // Reset form
        setNewAddress({
          emirate: '',
          location: '',
          contactNo: '',
          address: '',
          poBox: '',
          refNo: '',
          isDefault: false,
          locationUrl: '',
          emirateId: null,
          locationId: null
        });
        setSelectedEmirate(null);
        setLocationSearchQuery('');
        setLocations([]);
        setShowAddAddressForm(false);
        showSuccess('Address added successfully');
      } else {
        showError('Failed to save address');
      }
    } catch (error) {
      console.error('Error saving address:', error);
      showError('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = (addressId) => {
    console.log('handleDeleteAddress called with ID:', addressId);
    console.log('Available addresses:', addresses);
    
    // Find the address to get its details for the confirmation modal
    // Try different possible ID field names and types
    const address = addresses.find(addr => {
      const addrId = addr.Id || addr.id || addr.addressId;
      console.log('Checking address:', addr, 'with ID:', addrId, 'against:', addressId);
      return addrId == addressId; // Use == for type coercion (string vs number)
    });
    
    console.log('Found address:', address);
    
    if (!address) {
      console.error('Address not found with ID:', addressId);
      console.error('Available address IDs:', addresses.map(addr => ({
        Id: addr.Id,
        id: addr.id,
        addressId: addr.addressId,
        Address: addr.Address
      })));
      showError('Address not found');
      return;
    }
    
    setAddressToDelete(address);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) {
      console.error('No address to delete');
      showError('No address selected for deletion');
      return;
    }

    console.log('Address to delete:', addressToDelete);
    console.log('Address ID:', addressToDelete.id);

    try {
      setLoading(true);
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      
      if (!userData || !userData.Token) {
        showError('Please login first');
        return;
      }

      // Try different possible ID fields (prioritize Id with capital I as per backend structure)
      const addressId = addressToDelete.Id || addressToDelete.id || addressToDelete.addressId;
      
      if (!addressId) {
        console.error('No valid ID found for address:', addressToDelete);
        showError('Invalid address ID');
        return;
      }

      console.log('Calling DeleteClientAddress with ID:', addressId);
      const response = await MainService.DeleteClientAddress(addressId, userData.Token);
      
      console.log('Delete response:', response);
      
      if (response) {
        // Refresh addresses list from API
        await refreshAddresses();
        showSuccess('Address deleted successfully');
        setShowDeleteConfirm(false);
        setAddressToDelete(null);
      } else {
        showError('Failed to delete address');
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      showError('Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteAddress = () => {
    setShowDeleteConfirm(false);
    setAddressToDelete(null);
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      
      // Prepare the profile data (without bank details)
      const profileData = {
        Id: userData.ClientId,
        Name: formData.name,
        MobileNo1: formData.mobileNo1,
        MobileNo2: formData.mobileNo2,
        TelNo: formData.telNo,
        Company: formData.company,
        EmailId: formData.emailId,
        PayMode: formData.payMode,
        Photo: formData.photo
      };

      // Prepare bank details data
      const bankData = {
        BankName: formData.bankDetails.bankName,
        BankAccNo: formData.bankDetails.bankAccNo,
        BankAccName: formData.bankDetails.bankAccName,
        BranchName: formData.bankDetails.branchName,
        SwiftCode: formData.bankDetails.swiftCode,
        IBANNO: formData.bankDetails.ibanNo
      };

      // Update profile and bank details separately
      const [profileResponse, bankResponse] = await Promise.all([
        MainService.UpdateClientProfile(profileData, userData.Token),
        MainService.UpdateClientBankDetails(bankData, userData.Token)
      ]);
      
      if (profileResponse === "Success" && bankResponse === "Success") {
        setIsEditing(false);
        showSuccess('Profile and bank details updated successfully!');
        
        // Update the local storage with new data if needed
        const updatedUserData = { ...userData, ...profileData, ...bankData };
        localStorage.setItem('AINSOFT_DRIVER_TOKEN', JSON.stringify(updatedUserData));
        
        // Refresh client details
        await fetchClientDetails();
      } else {
        const errorMessage = profileResponse !== "Success" ? profileResponse : bankResponse;
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Separate function to update only bank details
  const handleUpdateBankDetails = async () => {
    try {
      setLoading(true);
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      
      // Prepare bank details data according to backend structure
      const bankData = {
        BankName: formData.bankDetails.bankName,
        BankAccNo: formData.bankDetails.bankAccNo,
        BankAccName: formData.bankDetails.bankAccName,
        BranchName: formData.bankDetails.branchName,
        SwiftCode: formData.bankDetails.swiftCode,
        IBANNO: formData.bankDetails.ibanNo,
        Id : userData.ClientId
      };

      const response = await MainService.UpdateClientBankDetails(bankData, userData.Token);
      
      if (response === "Success") {
        setIsEditingBank(false);
        showSuccess('Bank details updated successfully!');
        
        // Refresh client details to get updated data
        await fetchClientDetails();
      } else {
        showError(response);
      }
    } catch (error) {
      console.error('Error updating bank details:', error);
      showError('Failed to update bank details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      setLoading(true);
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      
      if (!userData || !userData.Token) {
        showError('Please login first');
        return;
      }

      const response = await MainService.MakeDefaultAddress(addressId, userData.Token);
      
      if (response) {
        // Refresh addresses list from API
        await refreshAddresses();
        showSuccess('Default address updated successfully');
      } else {
        showError('Failed to update default address');
      }
    } catch (error) {
      console.error('Error updating default address:', error);
      showError('Failed to update default address');
    } finally {
      setLoading(false);
    }
  };

  // Password change handler
  const handleChangePassword = async () => {
    try {
      // Validation
      if (!passwordData.newPassword || !passwordData.confirmPassword) {
        showError('Please fill in new password and confirmation');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        showError('New password and confirm password do not match');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        showError('New password must be at least 6 characters long');
        return;
      }

      setLoading(true);
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      
      // Prepare password data according to backend structure
      const passwordChangeData = {
        Password: passwordData.newPassword
      };

      const response = await MainService.ChangePassword(passwordChangeData, userData.Token);
      
      if (response) {
        showSuccess('Password changed successfully! You will be logged out automatically.');
        
        // Clear password form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowChangePassword(false);
        
        // Automatic logout after 2 seconds
        setTimeout(() => {
          localStorage.removeItem('AINSOFT_DRIVER_TOKEN');
          window.location.href = '/login';
        }, 2000);
      } else {
        showError(response || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBankDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [field]: value
      }
    }));
  };

  // Password strength validator
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    score = Object.values(checks).filter(Boolean).length;
    
    if (score <= 2) return { score, label: 'Weak', color: 'red' };
    if (score <= 3) return { score, label: 'Fair', color: 'yellow' };
    if (score <= 4) return { score, label: 'Good', color: 'blue' };
    return { score, label: 'Strong', color: 'green' };
  };

  const passwordStrength = getPasswordStrength(formData.password.newPassword);

  // Logout function
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    // Clear local storage
    localStorage.removeItem('AINSOFT_DRIVER_TOKEN');
    // Show success message
    showSuccess('Logged out successfully');
    // Redirect to login page
    window.location.href = '/login';
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
    setActiveTab('profile'); // Switch back to profile tab
  };

  const handleNestedChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleAddressChange = (addressId, field, value) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr =>
        addr.id === addressId ? { ...addr, [field]: value } : addr
      )
    }));
  };

  const addNewAddress = () => {
    const newAddress = {
      id: Date.now(),
      type: 'Other',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      isDefault: false
    };
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, newAddress]
    }));
  };

  const removeAddress = (addressId) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter(addr => addr.id !== addressId)
    }));
  };

  const setDefaultAddress = (addressId) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === addressId
      }))
    }));
  };


  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    { id: 'bank-details', label: 'Bank Details', icon: CreditCard },
    { id: 'change-password', label: 'Change Password', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: SettingsIcon },
    { id: 'sign-out', label: 'Sign Out', icon: LogOut }
  ];

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile</h1>
          <p className="text-gray-600">Manage your account preferences and settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === tab.id
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-primary-100 hover:to-primary-200 hover:text-primary-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {activeTab === 'profile' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit Profile</span>
                      </button>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setIsEditing(false)}
                          className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                        >
                          <X className="w-4 h-4" />
                          <span>Cancel</span>
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={loading}
                          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                        >
                          {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                          <Save className="w-4 h-4" />
                          )}
                          <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-6">
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter your full name"
                        />
                      ) : (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                          {formData.name || 'Not provided'}
                      </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mobile No 1</label>
                        {isEditing ? (
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                              type="tel"
                              value={formData.mobileNo1}
                              onChange={(e) => handleInputChange('mobileNo1', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pl-10"
                              placeholder="Enter mobile number 1"
                        />
                      </div>
                        ) : (
                          <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 flex items-center">
                            <Phone className="w-5 h-5 text-gray-400 mr-3" />
                            {formData.mobileNo1 || 'Not provided'}
                          </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mobile No 2</label>
                        {isEditing ? (
                      <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                              type="tel"
                              value={formData.mobileNo2}
                              onChange={(e) => handleInputChange('mobileNo2', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pl-10"
                              placeholder="Enter mobile number 2"
                            />
                          </div>
                        ) : (
                          <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 flex items-center">
                            <Phone className="w-5 h-5 text-gray-400 mr-3" />
                            {formData.mobileNo2 || 'Not provided'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tel No</label>
                      {isEditing ? (
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="tel"
                            value={formData.telNo}
                            onChange={(e) => handleInputChange('telNo', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pl-10"
                            placeholder="Enter telephone number"
                        />
                      </div>
                      ) : (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 flex items-center">
                          <Phone className="w-5 h-5 text-gray-400 mr-3" />
                          {formData.telNo || 'Not provided'}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email ID</label>
                      {isEditing ? (
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="email"
                            value={formData.emailId}
                            onChange={(e) => handleInputChange('emailId', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pl-10"
                            placeholder="Enter email address"
                          />
                        </div>
                      ) : (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 flex items-center">
                          <Mail className="w-5 h-5 text-gray-400 mr-3" />
                          {formData.emailId || 'Not provided'}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                      {isEditing ? (
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pl-10"
                            placeholder="Enter company name"
                          />
                        </div>
                      ) : (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 flex items-center">
                          <Building2 className="w-5 h-5 text-gray-400 mr-3" />
                          {formData.company || 'Not provided'}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                      {isEditing ? (
                        <select
                          value={formData.payMode}
                          onChange={(e) => handleInputChange('payMode', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="CASH">CASH</option>
                          <option value="CARD">CARD</option>
                        </select>
                      ) : (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {formData.payMode || 'Not provided'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                      <div className="flex items-center space-x-6">
                      <div className="relative">
                          <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-gray-200">
                            {formData.photo ? (
                              <img
                                src={formData.photo}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-center">
                                <Camera className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                                <p className="text-xs text-gray-500">No photo</p>
                              </div>
                            )}
                          </div>
                          {isEditing && (
                            <div className="absolute -top-2 -right-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                className="hidden"
                                id="photo-upload"
                              />
                              <label
                                htmlFor="photo-upload"
                                className="bg-primary-500 hover:bg-primary-600 text-white w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-200 shadow-lg"
                              >
                                <Camera className="w-4 h-4" />
                              </label>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          {isEditing ? (
                            <div>
                              <p className="text-sm text-gray-600 mb-2">Click the camera icon to upload a new photo</p>
                              <p className="text-xs text-gray-500">JPG, PNG up to 5MB</p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-gray-600">
                                {formData.photo ? 'Profile photo uploaded' : 'No profile photo uploaded'}
                              </p>
                              <p className="text-xs text-gray-500">Click Edit to change photo</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'addresses' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Addresses</h2>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={refreshAddresses}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                        title="Refresh addresses"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>Refresh</span>
                      </button>
                      <button
                        onClick={() => setShowAddAddressForm(true)}
                        className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Address</span>
                      </button>
                    </div>
                  </div>

                  {/* Add Address Form */}
                  {showAddAddressForm && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8">
                      <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                            <Plus className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">Add New Address</h3>
                            <p className="text-sm text-gray-500">Fill in the details to add a new delivery address</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowAddAddressForm(false);
                            // Reset form state when closing
                            setNewAddress({
                              emirate: '',
                              location: '',
                              contactNo: '',
                              address: '',
                              poBox: '',
                              refNo: '',
                              isDefault: false,
                              locationUrl: '',
                              emirateId: null,
                              locationId: null
                            });
                            setSelectedEmirate(null);
                            setLocationSearchQuery('');
                            setLocations([]);
                            setShowLocationDropdown(false);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-green-600" />
                              Emirate <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className="relative">
                              <select
                                value={newAddress.emirateId || ''}
                                onChange={(e) => {
                                  const emirateId = parseInt(e.target.value);
                                  const selectedEmirate = emirates.find(em => em.Id === emirateId);
                                  if (selectedEmirate) {
                                    handleEmirateSelect(selectedEmirate);
                                  }
                                }}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white appearance-none cursor-pointer"
                              >
                                <option value="">Select Emirate</option>
                                {emirates.map((emirate) => (
                                  <option key={emirate.Id} value={emirate.Id}>
                                    {emirate.Name}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Building2 className="w-4 h-4 mr-2 text-green-600" />
                              Location <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className="relative location-dropdown-container">
                              <input
                                type="text"
                                value={locationSearchQuery}
                                onChange={(e) => handleLocationSearch(e.target.value)}
                                onFocus={() => setShowLocationDropdown(true)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                                placeholder={selectedEmirate ? "Search location..." : "Select emirate first"}
                                disabled={!selectedEmirate}
                              />
                              {showLocationDropdown && filteredLocations.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                  {filteredLocations.map((location) => (
                                    <div
                                      key={location.Id}
                                      onClick={() => handleLocationSelect(location)}
                                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="text-sm font-medium text-gray-900">{location.Name}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {showLocationDropdown && filteredLocations.length === 0 && locationSearchQuery && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg">
                                  <div className="px-4 py-3 text-sm text-gray-500">
                                    No locations found
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Phone className="w-4 h-4 mr-2 text-green-600" />
                              Contact No
                            </label>
                            <input
                              type="tel"
                              value={newAddress.contactNo}
                              onChange={(e) => handleNewAddressChange('contactNo', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter contact number"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Building2 className="w-4 h-4 mr-2 text-green-600" />
                              PO Box
                            </label>
                            <input
                              type="text"
                              value={newAddress.poBox}
                              onChange={(e) => handleNewAddressChange('poBox', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter PO Box"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Hash className="w-4 h-4 mr-2 text-green-600" />
                              Ref No
                            </label>
                            <input
                              type="text"
                              value={newAddress.refNo}
                              onChange={(e) => handleNewAddressChange('refNo', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter reference number"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-green-600" />
                              Location URL
                            </label>
                            <input
                              type="url"
                              value={newAddress.locationUrl}
                              onChange={(e) => handleNewAddressChange('locationUrl', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter location URL"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                            <MapPin className="w-4 h-4 mr-2 text-green-600" />
                            Address <span className="text-red-500 ml-1">*</span>
                          </label>
                        <textarea
                            value={newAddress.address}
                            onChange={(e) => handleNewAddressChange('address', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white resize-none"
                            rows="4"
                            placeholder="Enter full address"
                        />
                      </div>
                        
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newAddress.isDefault}
                              onChange={(e) => handleNewAddressChange('isDefault', e.target.checked)}
                              className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                            />
                            <div>
                              <span className="text-sm font-semibold text-gray-800">Set as default address</span>
                              <p className="text-xs text-gray-500 mt-1">This address will be used as the primary delivery location</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setShowAddAddressForm(false)}
                          className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddAddress}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Address</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Address List - Card Layout */}
                  {addresses.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                      <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <MapPin className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No addresses found</h3>
                      <p className="text-gray-500 mb-6">Start by adding your first address to manage your delivery locations</p>
                      <button
                        onClick={() => setShowAddAddressForm(true)}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 mx-auto shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Add Your First Address</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {addresses.map((address, index) => (
                        <div key={address.Id || address.id || index} className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden">
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                                  <MapPin className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {address.Emirate?.Name || 'N/A'} - {address.Location?.Name || 'N/A'}
                                  </h3>
                                  <p className="text-sm text-gray-500">Address #{index + 1}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {address.IsDefault && (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                    Default
                                  </span>
                                )}
                                <div className="flex space-x-1">
                                  {!address.IsDefault && (
                                    <button
                                      onClick={() => handleSetDefaultAddress(address.Id || address.id || index)}
                                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200"
                                      title="Set as default"
                                    >
                                      <User className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteAddress(address.Id || address.id || index)}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                    title="Delete address"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <Phone className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Contact No</p>
                                  <p className="text-sm text-gray-900">{address.ContactNo || 'Not provided'}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">PO Box</p>
                                  <p className="text-sm text-gray-900">{address.POBOX || 'Not provided'}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <Hash className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ref No</p>
                                  <p className="text-sm text-gray-900">{address.RefNo || 'Not provided'}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gray-50 rounded-xl p-4">
                              <div className="flex items-start space-x-3">
                                <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <MapPin className="w-4 h-4 text-gray-600" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Full Address</p>
                                  <p className="text-sm text-gray-900 leading-relaxed">{address.Address || 'Not provided'}</p>
                                </div>
                              </div>
                            </div>
                            
                            {address.LocationUrl && (
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <MapPin className="w-4 h-4 text-gray-600" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location URL</p>
                                    <a 
                                      href={address.LocationUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                      View on Map
                                    </a>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'bank-details' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Bank Details</h2>
                    {!isEditingBank && (
                      <button
                        onClick={() => setIsEditingBank(true)}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit Bank Details</span>
                      </button>
                    )}
                  </div>

                  {!isEditingBank ? (
                    // Read-only view
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Bank Name</p>
                              <p className="text-lg font-semibold text-gray-900">{formData.bankDetails.bankName || 'Not provided'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                              <Hash className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Account Number</p>
                              <p className="text-lg font-semibold text-gray-900">{formData.bankDetails.bankAccNo ? '****' + formData.bankDetails.bankAccNo.slice(-4) : 'Not provided'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                              <User className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Account Holder Name</p>
                              <p className="text-lg font-semibold text-gray-900">{formData.bankDetails.bankAccName || 'Not provided'}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-6">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Branch Name</p>
                              <p className="text-lg font-semibold text-gray-900">{formData.bankDetails.branchName || 'Not provided'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                              <CreditCard className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">IBAN</p>
                              <p className="text-lg font-semibold text-gray-900 font-mono">{formData.bankDetails.ibanNo || 'Not provided'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                              <Hash className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">SWIFT Code</p>
                              <p className="text-lg font-semibold text-gray-900 font-mono">{formData.bankDetails.swiftCode || 'Not provided'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Edit form
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                      <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                            <Edit className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">Edit Bank Details</h3>
                            <p className="text-sm text-gray-500">Update your banking information</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditingBank(false)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Building2 className="w-4 h-4 mr-2 text-green-600" />
                              Bank Name
                            </label>
                            <input
                              type="text"
                              value={formData.bankDetails.bankName}
                              onChange={(e) => handleBankDetailsChange('bankName', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter bank name"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Hash className="w-4 h-4 mr-2 text-green-600" />
                              Account Number
                            </label>
                            <input
                              type="text"
                              value={formData.bankDetails.bankAccNo}
                              onChange={(e) => handleBankDetailsChange('bankAccNo', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter account number"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Building2 className="w-4 h-4 mr-2 text-green-600" />
                              Branch Name
                            </label>
                            <input
                              type="text"
                              value={formData.bankDetails.branchName}
                              onChange={(e) => handleBankDetailsChange('branchName', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter branch name"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <User className="w-4 h-4 mr-2 text-green-600" />
                              Account Holder Name
                            </label>
                            <input
                              type="text"
                              value={formData.bankDetails.bankAccName}
                              onChange={(e) => handleBankDetailsChange('bankAccName', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                              placeholder="Enter account holder name"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <CreditCard className="w-4 h-4 mr-2 text-green-600" />
                              IBAN
                            </label>
                            <input
                              type="text"
                              value={formData.bankDetails.ibanNo}
                              onChange={(e) => handleBankDetailsChange('ibanNo', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white font-mono"
                              placeholder="Enter IBAN"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                              <Hash className="w-4 h-4 mr-2 text-green-600" />
                              SWIFT Code
                            </label>
                            <input
                              type="text"
                              value={formData.bankDetails.swiftCode}
                              onChange={(e) => handleBankDetailsChange('swiftCode', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white font-mono"
                              placeholder="Enter SWIFT code"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setIsEditingBank(false)}
                          className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateBankDetails}
                          disabled={loading}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                          {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'change-password' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>
                  <div className="max-w-2xl">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                            <Lock className="w-4 h-4 mr-2 text-green-600" />
                            New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                            placeholder="Enter new password"
                          />
                          
                          {/* Password Strength Indicator */}
                          {formData.password.newPassword && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Password Strength:</span>
                                <span className={`text-sm font-semibold ${
                                  passwordStrength.color === 'red' ? 'text-red-600' :
                                  passwordStrength.color === 'yellow' ? 'text-yellow-600' :
                                  passwordStrength.color === 'blue' ? 'text-blue-600' :
                                  'text-green-600'
                                }`}>
                                  {passwordStrength.label}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    passwordStrength.color === 'red' ? 'bg-red-500' :
                                    passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                                    passwordStrength.color === 'blue' ? 'bg-blue-500' :
                                    'bg-green-500'
                                  }`}
                                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                            <Lock className="w-4 h-4 mr-2 text-green-600" />
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                            placeholder="Confirm new password"
                          />
                          
                          {/* Password Match Indicator */}
                          {passwordData.confirmPassword && (
                            <div className="mt-2">
                              {passwordData.newPassword === passwordData.confirmPassword ? (
                                <div className="flex items-center text-green-600">
                                  <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                  </div>
                                  <span className="text-sm font-medium">Passwords match</span>
                                </div>
                              ) : (
                                <div className="flex items-center text-red-600">
                                  <div className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2">
                                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                                  </div>
                                  <span className="text-sm font-medium">Passwords do not match</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Password Requirements */}
                        <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
                          <h4 className="text-sm font-semibold text-green-900 mb-4 flex items-center">
                            <Shield className="w-4 h-4 mr-2" />
                            Password Requirements:
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className={`flex items-center space-x-2 ${
                              passwordData.newPassword && passwordData.newPassword.length >= 6 ? 'text-green-700' : 'text-gray-600'
                            }`}>
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordData.newPassword && passwordData.newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-300'
                              }`}>
                                {passwordData.newPassword && passwordData.newPassword.length >= 6 && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="text-sm">At least 6 characters long</span>
                            </div>
                            
                            <div className={`flex items-center space-x-2 ${
                              passwordData.newPassword && /[a-z]/.test(passwordData.newPassword) ? 'text-green-700' : 'text-gray-600'
                            }`}>
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordData.newPassword && /[a-z]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                              }`}>
                                {passwordData.newPassword && /[a-z]/.test(passwordData.newPassword) && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="text-sm">Contains lowercase letters</span>
                            </div>
                            
                            <div className={`flex items-center space-x-2 ${
                              passwordData.newPassword && /[A-Z]/.test(passwordData.newPassword) ? 'text-green-700' : 'text-gray-600'
                            }`}>
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordData.newPassword && /[A-Z]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                              }`}>
                                {passwordData.newPassword && /[A-Z]/.test(passwordData.newPassword) && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="text-sm">Contains uppercase letters</span>
                            </div>
                            
                            <div className={`flex items-center space-x-2 ${
                              passwordData.newPassword && /\d/.test(passwordData.newPassword) ? 'text-green-700' : 'text-gray-600'
                            }`}>
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordData.newPassword && /\d/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                              }`}>
                                {passwordData.newPassword && /\d/.test(passwordData.newPassword) && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="text-sm">Contains numbers</span>
                            </div>
                            
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                          <button
                            onClick={() => {
                              setPasswordData({
                                currentPassword: '',
                                newPassword: '',
                                confirmPassword: ''
                              });
                            }}
                            className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                          >
                            Clear
                          </button>
                          <button
                            onClick={handleChangePassword}
                            disabled={loading}
                            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                          >
                            {loading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            <span>{loading ? 'Updating...' : 'Update Password'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Notification Preferences (Coming Soon)</h2>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">Email Notifications</h3>
                          <p className="text-sm text-gray-600">Receive updates via email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.notifications.email}
                            onChange={(e) => handleNestedChange('notifications', 'email', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">SMS Notifications</h3>
                          <p className="text-sm text-gray-600">Receive updates via SMS</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.notifications.sms}
                            onChange={(e) => handleNestedChange('notifications', 'sms', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">Push Notifications</h3>
                          <p className="text-sm text-gray-600">Receive push notifications</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.notifications.push}
                            onChange={(e) => handleNestedChange('notifications', 'push', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">Delivery Updates</h3>
                          <p className="text-sm text-gray-600">Get notified about delivery status changes</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.notifications.deliveryUpdates}
                            onChange={(e) => handleNestedChange('notifications', 'deliveryUpdates', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {activeTab === 'preferences' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Preferences(Coming Soon)</h2>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                        <select
                          value={formData.preferences.language}
                          onChange={(e) => handleNestedChange('preferences', 'language', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="English">English</option>
                          <option value="Spanish">Spanish</option>
                          <option value="French">French</option>
                          <option value="German">German</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                        <select
                          value={formData.preferences.theme}
                          onChange={(e) => handleNestedChange('preferences', 'theme', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="Light">Light</option>
                          <option value="Dark">Dark</option>
                          <option value="Auto">Auto</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}



      </div>
      </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Confirm Sign Out</h3>
                <p className="text-sm text-gray-500">Are you sure you want to sign out?</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">You will be logged out of your account</p>
                  <p className="text-xs text-red-600 mt-1">All unsaved changes will be lost and you'll need to sign in again to access your account.</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelLogout}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <LogOut className="w-4 h-4" />
                <span>Yes, Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Address Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Delete Address</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">Are you sure you want to delete this address?</p>
                  <p className="text-xs text-red-600 mt-1">This will permanently remove the address from your account.</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteAddress}
                disabled={loading}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('Delete button clicked');
                  confirmDeleteAddress();
                }}
                disabled={loading}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Yes, Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 